import type { BranchChangesets } from '@festina/configuration-general-types/configuration'
import { ConfigurationRepositoryComparisonsService_configurationComparisonServiceGetGetMergeComparison } from '@festina/configuration-general-types/configuration/hooks/ConfigurationRepositoryComparisonsService_configurationComparisonServiceGetGetMergeComparison'
import type { BranchKey } from '@festina-life/api'
import { assertNotEmpty, notEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback, useMemo, useState } from 'react'

import { useConciseBranch } from '../../../../uiModels/branches/useConciseBranch'
import type { MergeCompareStatus } from './types'

const useLazyFetchMergeComparison =
  ConfigurationRepositoryComparisonsService_configurationComparisonServiceGetGetMergeComparison.useLazyQuery

export const useMergeComparison = (targetBranchKey: BranchKey) => {
  const translate = useTranslate()
  const conciseBranch = useConciseBranch({
    branchName: targetBranchKey.name,
    server: targetBranchKey.server,
    shouldFallbackToStandard: false,
  })
  const [status, setStatus] = useState<MergeCompareStatus>()
  const [error, setError] = useState('')
  const [branchChangesets, setBranchChangesets] = useState<BranchChangesets>()
  const [conflictChangesets, setConflictChangesets] =
    useState<BranchChangesets>()
  const { mutateAsync: triggerMergeComparison } = useLazyFetchMergeComparison()

  const reportFailure = useCallback((reason: string) => {
    setStatus('error')
    setError(reason)
  }, [])

  assertNotEmpty(conciseBranch)

  const compare = useCallback(
    async (sourceBranchName: string) => {
      assertNotEmpty(sourceBranchName)
      setError('')
      setBranchChangesets(undefined)
      setConflictChangesets(undefined)
      setStatus('comparing')

      try {
        const mergeComparison = await triggerMergeComparison({
          componentName: targetBranchKey.server,
          lhsBranchName: sourceBranchName,
          rhsBranchName: targetBranchKey.name,
        })

        const rhsConflictCount =
          mergeComparison?.conflicts?.reduce(
            (acc, conflict) => acc + (conflict?.rhsChangesets?.length ?? 0),
            0,
          ) ?? 0

        if (!mergeComparison) {
          reportFailure(
            translate(
              'branchDetailsPanel.tabs.manage.merge.error.noPrefixComparison',
            ),
          )
        } else if (!mergeComparison.missing?.length && !rhsConflictCount) {
          reportFailure(
            translate('branchDetailsPanel.tabs.manage.merge.error.noDiff', {
              sourceBranchName,
              targetBranchName: targetBranchKey.name,
            }),
          )
        } else {
          const missingBranchChangesets = {
            _type: 'BranchChangesets',
            branchChangesets: mergeComparison.missing?.filter(notEmpty) ?? [],
          }
          setBranchChangesets(missingBranchChangesets)

          const nonEmptyConflictChangesets = mergeComparison.conflicts?.flatMap(
            (conflict) => conflict?.rhsChangesets?.filter(notEmpty) ?? [],
          )

          if (nonEmptyConflictChangesets?.length) {
            setConflictChangesets({
              _type: 'BranchChangesets',
              branchChangesets: nonEmptyConflictChangesets,
            } satisfies BranchChangesets)
          }

          setStatus('success')
        }
      } catch {
        reportFailure(
          translate('branchDetailsPanel.tabs.manage.merge.error.unknown'),
        )
      }
    },
    [
      triggerMergeComparison,
      targetBranchKey.server,
      targetBranchKey.name,
      reportFailure,
      translate,
    ],
  )

  return useMemo(
    () => ({
      compare,
      status,
      error,
      branchChangesets,
      conflictChangesets,
    }),
    [compare, status, error, branchChangesets, conflictChangesets],
  )
}
